<!-- /news/2026/04-30 (/news/2026/04-30-b for a second item that day): one email or old blog
     post, rendered as page content (extracted by fullstack) -->

<template>
  <NewsLayout>
    <!-- breadcrumb: News / 2026 / April 30; the "News" heading as on the year's page -->
    <h3 class="breadcrumb news-heading">
      <nav aria-label="Breadcrumb">
        <router-link to="/news">News</router-link>
        <span class="separator">/</span>
        <router-link :to="`/news/${year}`" class="crumb-year">{{ year }}</router-link>
        <span class="separator">/</span>
        <span class="crumb-date" aria-current="page">{{ dayLabel }}</span>
      </nav>
    </h3>
    <p v-if="isMissing">This news item could not be found.</p>
    <article v-else-if="item" class="news-item news-region">
      <h4>{{ item.title }}</h4>
      <!-- an email's pre-header (the date is already in the breadcrumb); not for old blog
           posts, whose excerpt is their own first paragraph -->
      <p v-if="item.excerpt && item.source !== 'blog'" class="lede">{{ item.excerpt }}</p>
      <!-- eslint-disable-next-line vue/no-v-html -- trusted: VIZBI's own emails and posts -->
      <div
        v-if="item.html"
        class="message"
        @click="followInternalLink($event, router)"
        v-html="item.html" />
      <!-- an old blog post whose text hasn't been fetched yet (scripts/update/blog) -->
      <p v-else-if="item.url"><a :href="item.url">Read the original post</a></p>
    </article>
  </NewsLayout>
</template>

<script setup>
import {useRoute, useRouter} from "vue-router";
import {ref} from "vue";
import NewsLayout from "@/components/News/Layout.vue";
import {
  fetchNewsItem, fetchNewsYear, findNewsItem, followInternalLink, getNewsMonthDay
} from "@/composables/useNews";
import {
  ORGANIZATION_ID, getPageUrl, getShortDescription, setPageMeta
} from "@/composables/usePageMeta";

const route = useRoute();
const router = useRouter();
// fixed per page (each path is its own kept-alive page)
const {year, day} = route.params;
const pagePath = route.path;
const [, monthDay, letter] = day.match(/^(\d{2}-\d{2})(?:-([a-z]))?$/);
const dayLabel = `${getNewsMonthDay(`${year}-${monthDay}`)}${letter ? ` (${letter})` : ""}`;
const item = ref(null);
const isMissing = ref(false);

function getArticleData(entry) {
  return {
    "@type": "Article",
    headline: entry.title,
    datePublished: entry.date,
    url: getPageUrl(pagePath),
    publisher: {"@id": ORGANIZATION_ID}
  };
}

function setItemMeta(entry) {
  const text = entry.excerpt || entry.html?.replace(/<[^>]+>/g, " ");
  setPageMeta(pagePath, {
    title: `${entry.title} - VIZBI News`,
    description: getShortDescription(text),
    type: "article",
    structuredData: [getArticleData(entry)]
  });
}

// find the item in its year's list (which tells same-date items apart), then fetch its body
async function loadItem() {
  const entry = findNewsItem(year, day, await fetchNewsYear(year));
  item.value = entry ? await fetchNewsItem(entry.slug) : null;
  isMissing.value = !item.value;
  if (item.value) setItemMeta(item.value);
}

loadItem();
</script>

<style scoped>
/* News / 2026 / April 30 */
/* links as the year navigation on /2026 (Navigation/Secondary.vue): grey, never underlined,
   lighter on hover; the current page in brand green */
.breadcrumb a,
.breadcrumb a:hover {
  text-decoration: none;
}
.breadcrumb a {
  color: var(--text-color-subtle);
}
.breadcrumb a:hover {
  color: var(--text-color-less-subtle);
}
.separator,
.breadcrumb a.crumb-year {
  font-weight: 400;
}
.separator {
  color: var(--text-color-subtle); /* as the links: the divider on /2026 is this grey */
}
/* the current page */
.crumb-date {
  color: var(--brand-color);
}
.separator {
  margin: 0 0.3em;
}
/* one black region (News/message.css), its text at the emails' 600px, as on /news */
.news-item {
  padding: 0.8rem 0.7rem;
}
.news-item > * {
  max-width: var(--news-text-width);
}
.news-item h4 {
  margin-top: 0;
}
/* the pre-header as a lede: set apart from the title above and the body below */
.lede {
  margin: 0.2rem 0 1.2rem;
  /* italic and a darker grey set it apart from the title and the body */
  color: color-mix(in srgb, var(--text-color-less-subtle) 70%, var(--text-color-subtle));
  font-size: 1.1em;
  font-style: italic;
  line-height: 1.4;
}
</style>
